import * as yup from 'yup';
import {hasCustomerInput, isCustomerIdentityComplete} from 'packages/common/components/customer/utils/customerIdentity';
import {getCurrencySymbol} from '../../../../config/currencies';

// Validation de l'étape 1 : Type de bon
const createStep1Schema = (voucherTypes, selectedTypeId, currency = 'EUR', isMultiMode = false) => {
  // Mode multi-prestations : valide la composition uniquement
  if (isMultiMode) {
    return yup.object().shape({
      includedServices: yup.array()
          .of(yup.object())
          .min(1, 'Ajoutez au moins une prestation au panier'),
    });
  }

  const selectedType = getSelectedVoucherType(voucherTypes, selectedTypeId);

  return yup.object().shape({
    giftVoucherTypeId: yup.string()
        .required('Veuillez sélectionner un type de bon cadeau'),
    amount: yup.number()
        .when(['giftVoucherTypeId', 'selectedVariantName'], {
          is: (typeId, variantName) => {
            const type = getSelectedVoucherType(voucherTypes, typeId);
            return type?.amountType === 'free' || (type?.amountType === 'fixed' && !variantName);
          },
          then: () => yup.number()
              .required('Le montant est obligatoire')
              .typeError('Vous devez entrer un nombre')
              .min(
                  Math.max(selectedType?.minAmount / 100 || 0, 0.01),
                  `Le montant minimum est ${(Math.max(selectedType?.minAmount / 100 || 0, 0.01)).toLocaleString('fr-FR')}${getCurrencySymbol(currency)}`,
              )
              .max(
            selectedType?.maxAmount ?
              Math.min(selectedType.maxAmount / 100, 100000) :
              100000,
            `Le montant maximum est ${Math.min(selectedType?.maxAmount / 100 || 100000, 100000).toLocaleString('fr-FR')}${getCurrencySymbol(currency)}`,
              ),
          otherwise: () => yup.mixed().notRequired(),
        }),
    selectedVariantName: yup.string()
        .when('giftVoucherTypeId', {
          is: (typeId) => {
            const type = getSelectedVoucherType(voucherTypes, typeId);
            return type?.amountType === 'matrix';
          },
          then: () => yup.string().required('Veuillez sélectionner une combinaison'),
          otherwise: () => yup.mixed().notRequired(),
        }),
  });
};

// Validation de l'étape 2 : Personnalisation (champs optionnels)
const step2Schema = yup.object().shape({
  from: yup.string()
      .max(75, 'Le champ doit avoir au maximum 75 caractères')
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ0-9\-,_&' ]*$/, {
        message: 'Un caractère n\'est pas autorisé',
        excludeEmptyString: true,
      }),
  to: yup.string()
      .max(75, 'Le champ doit avoir au maximum 75 caractères')
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ0-9\-,_&' ]*$/, {
        message: 'Un caractère n\'est pas autorisé',
        excludeEmptyString: true,
      }),
  customMessage: yup.string()
      .max(250, 'Le message ne doit pas dépasser 250 caractères'),
});

// Validation de l'étape 3 : Liaison client. Le client est optionnel, mais une
// fiche commencée doit être complète (nom + contact) pour être créée avec le bon,
// ou vidée pour continuer sans client.
const step3Schema = yup.object().shape({
  customer: yup
      .mixed()
      .nullable()
      .test(
          'customer-draft-complete',
          'Complétez la fiche (nom et un email ou téléphone) ou videz les champs pour continuer sans client',
          (customer) => !customer || Boolean(customer._id) || !hasCustomerInput(customer) || isCustomerIdentityComplete(customer),
      ),
});

// Fonction utilitaire pour obtenir les détails du type de bon sélectionné
const getSelectedVoucherType = (voucherTypes, selectedTypeId) =>
  voucherTypes.find((type) => type._id === selectedTypeId);

// Fonction de validation principale
export const validateStep = async (step, formData, setErrors, voucherTypes, currency = 'EUR') => {
  try {
    let schema;
    switch (step) {
      case 0:
        schema = createStep1Schema(
            voucherTypes,
            formData.giftVoucherTypeId,
            currency,
            formData.compositionMode === 'multi',
        );
        break;
      case 1:
        schema = step2Schema;
        break;
      case 2:
        schema = step3Schema;
        break;
      default:
        return true;
    }

    await schema.validate(formData, {abortEarly: false});
    setErrors({});
    return true;
  } catch (error) {
    if (error.inner) {
      const newErrors = {};
      error.inner.forEach((err) => {
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
    }
    return false;
  }
};
